import { useState, type ChangeEvent } from "react";
import { Alert, Button, Card, Input } from "../../ds";
import { api, errorText, type Model, type ModelInput } from "../../lib/api";

// Formular zum Anlegen und Bearbeiten eines Router-Modells. Schreibt selbst und
// meldet dem Aufrufer per onSaved, dass die Liste neu geladen werden muss.

type FormState = ModelInput & { id?: string };

const BLANK: FormState = {
  name: "",
  target: "ramips/mt7620",
  profile: "zbtlink_zbt-we826-16m",
  notes: "",
};

const fromModel = (m: Model): FormState => ({
  id: m.id,
  name: m.name,
  target: m.target,
  profile: m.profile,
  notes: m.notes ?? "",
});

interface ModelFormProps {
  /** `null` = neues Modell anlegen. */
  model: Model | null;
  onCancel: () => void;
  onSaved: () => void;
}

export function ModelForm({ model, onCancel, onSaved }: ModelFormProps) {
  const [form, setForm] = useState<FormState>(() => (model ? fromModel(model) : { ...BLANK }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof FormState) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const input: ModelInput = {
        name: form.name.trim(),
        target: form.target.trim(),
        profile: form.profile.trim(),
        notes: form.notes?.trim() ?? "",
      };
      if (form.id) await api.updateModel(form.id, input);
      else await api.createModel(input);
      onSaved();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  }

  const complete = form.name.trim() && form.target.trim() && form.profile.trim();

  return (
    <Card
      title={form.id ? `Modell bearbeiten — ${form.id}` : "Neues Modell"}
      subtitle="Target und Profil sind der Maßstab für hochgeladene Images — sie müssen zu dem passen, was OpenWrt in die Image-Metadaten schreibt."
    >
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input label="Name" value={form.name} onChange={set("name")} placeholder="ZBT WE826-16M" />
        <Input
          label="Target"
          value={form.target}
          onChange={set("target")}
          placeholder="ramips/mt7620"
        />
        <Input
          label="Profil / Board"
          value={form.profile}
          onChange={set("profile")}
          placeholder="zbtlink_zbt-we826-16m"
        />
        <Input
          label="Notiz (optional)"
          value={form.notes ?? ""}
          onChange={set("notes")}
          placeholder="z.B. Variante mit LTE-Modem"
        />
      </div>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button
          variant="primary"
          iconName="check"
          loading={saving}
          disabled={!complete}
          onClick={() => void save()}
        >
          Speichern
        </Button>
      </div>
    </Card>
  );
}
